import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import {
  hasOtherPlans,
  type PlanChoicePurpose,
} from '@/helpers/subscription-plans';
import type { SubscriptionCode } from '@/state/subscription-codes';
import useSubscriptionGroups from '@/state/subscription-groups';

export type PlanChoiceState = 'unknown' | 'has_other' | 'only_current';

// Three answers, not two, because the button gates and the redirect need
// different stances on a groups read that has not landed. Retries and refetch
// on mount are off, so one failed read would otherwise settle the question for
// the rest of the session.
export function usePlanChoiceState(
  subscriptionCode: SubscriptionCode | undefined,
  purpose: PlanChoicePurpose = 'change'
): PlanChoiceState {
  const { data, isLoading, error } = useSubscriptionGroups({
    include_subscriptions: true,
  });

  if (!subscriptionCode) return 'only_current';
  if (isLoading || error || !data) return 'unknown';

  return hasOtherPlans(data, subscriptionCode, purpose)
    ? 'has_other'
    : 'only_current';
}

// Fails open: an unknown answer still shows the action. The destination sends
// the member back when there is nothing to choose, so a wrong "yes" costs a
// redirect, while a wrong "no" hides an action the member can take.
export function useCanChangePlan(
  subscriptionCode: SubscriptionCode | undefined,
  purpose: PlanChoicePurpose = 'change'
): boolean {
  const state = usePlanChoiceState(subscriptionCode, purpose);

  return subscriptionCode ? state !== 'only_current' : false;
}

// Fails closed by comparison: only a settled "nothing to choose" redirects, so
// a failed groups read never bounces a member off a page she can use. Returns
// true so the caller keeps its skeleton up instead of flashing a one-row
// picker while the replace is in flight.
export function useRedirectWithoutPlanChoices(
  subscriptionCode: SubscriptionCode,
  purpose: PlanChoicePurpose = 'change'
): boolean {
  const router = useRouter();
  const state = usePlanChoiceState(subscriptionCode, purpose);
  const shouldRedirect = state === 'only_current';

  useEffect(() => {
    if (shouldRedirect) router.replace('/dashboard/credits');
  }, [shouldRedirect, router]);

  return shouldRedirect;
}
